import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}

/* ============================================================
 * RoverOak ATC hardening — v2 (Jul 15, 2026; supersedes Jul 8 block)
 * Covers BOTH custom PDPs (Trailhound #ro-product-form, Finn #finn-form)
 * without touching the inline buy-box scripts in the templates.
 *
 * 1) DOUBLE-SUBMIT GUARD — sticky bars call form.requestSubmit(),
 *    which ignores the disabled state of the in-form submit button.
 *    Rapid double-taps fired two /cart/add.js posts -> duplicate
 *    items (~15% of Jul 7-8 orders showed the pattern).
 *    A capture-phase listener swallows re-entrant submits while an
 *    add is in flight and disables any sticky trigger buttons.
 *
 * 2) PACK ACTIVATION (Trailhound only) — clicking a color dot on a
 *    non-active pack card selected the color but left the previous
 *    pack active. A dot tap now activates its own pack card first.
 * ============================================================ */
(function () {
  var GUARDED_FORMS = ['ro-product-form', 'finn-form'];
  var adding = false;
  var failsafe = null;

  function stickyButtons() {
    return document.querySelectorAll('#ro-sticky-btn,[data-ro-sticky-btn]');
  }
  function release() {
    adding = false;
    if (failsafe) { clearTimeout(failsafe); failsafe = null; }
    stickyButtons().forEach(function (s) {
      s.disabled = false; s.style.opacity = '';
      if (s.dataset.roLabel) { s.textContent = s.dataset.roLabel; delete s.dataset.roLabel; }
    });
  }

  // (1) Re-entrancy guard — capture phase runs before the form's own listener
  document.addEventListener('submit', function (e) {
    if (!e.target || GUARDED_FORMS.indexOf(e.target.id) === -1) return;
    if (adding) { e.preventDefault(); e.stopImmediatePropagation(); return; }
    adding = true;
    stickyButtons().forEach(function (s) {
      s.disabled = true; s.style.opacity = '.55';
      if (!s.dataset.roLabel) s.dataset.roLabel = s.textContent;
      s.textContent = 'Adding…';
    });
    // Release when the cart confirms the add, or after 4s (the error path
    // shows an alert and never fires cart:update)
    failsafe = setTimeout(release, 4000);
  }, true);
  document.addEventListener('cart:update', release);

  // (2) A color-dot tap activates its own pack card first (Trailhound PDP)
  document.addEventListener('click', function (e) {
    var dot = e.target && e.target.closest ? e.target.closest('.ro-bswatch') : null;
    if (!dot) return;
    var card = dot.closest('.ro-bundle-option');
    // Synthetic click on the card chrome = the theme's own activation path
    if (card && !card.classList.contains('is-active')) card.click();
  }, true);
})();
